/**
 * Integrations data access: TanStack Query hooks over the typed client. Every write is an action
 * on the server (`perform`) and returns its result (message, data, links).
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type { ActionOut, DeliveryStatus, WebhookCreate, WebhookPatch } from './types';

export const integrationsKeys = {
  all: ['integrations'] as const,
  webhooks: (p: object) => ['integrations', 'webhooks', p] as const,
  webhook: (id: string) => ['integrations', 'webhook', id] as const,
  deliveries: (p: object) => ['integrations', 'deliveries', p] as const,
  delivery: (id: string) => ['integrations', 'delivery', id] as const,
  catalogue: ['integrations', 'event-types'] as const,
};

const PAGE = 100;
const next = (last: { next_cursor?: string | null }) => last.next_cursor ?? undefined;

export interface WebhooksQuery {
  status?: 'active' | 'disabled' | 'current';
  q?: string;
  sort?: 'name' | '-name' | 'created_at' | '-created_at';
}

export function useWebhooks(p: WebhooksQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: integrationsKeys.webhooks(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/integrations/webhooks', { params: { query: { ...p, cursor: pageParam, limit: PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useWebhook(id: string | undefined) {
  return useQuery({
    queryKey: integrationsKeys.webhook(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/integrations/webhooks/{webhook_id}', { params: { path: { webhook_id: id ?? '' } } })),
    enabled: !!id,
  });
}

export interface DeliveriesQuery {
  webhook_id?: string;
  status?: DeliveryStatus;
  event_type?: string;
}

export function useDeliveries(p: DeliveriesQuery, enabled = true, limit = PAGE) {
  return useInfiniteQuery({
    queryKey: integrationsKeys.deliveries({ ...p, limit }),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/integrations/deliveries', { params: { query: { ...p, cursor: pageParam, limit } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDelivery(id: string | undefined) {
  return useQuery({
    queryKey: integrationsKeys.delivery(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/integrations/deliveries/{delivery_id}', { params: { path: { delivery_id: id ?? '' } } })),
    enabled: !!id,
  });
}

/** Every event type an endpoint can subscribe to (and the module prefixes). */
export function useEventCatalogue(enabled = true) {
  return useQuery({
    queryKey: integrationsKeys.catalogue,
    queryFn: () => unwrap(api.GET('/api/integrations/event-types')),
    staleTime: 10 * 60_000,
    enabled,
  });
}

// ---------------------------------------------------------------- writes

function useWrite<V>(fn: (v: V) => Promise<ActionOut>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: integrationsKeys.all }),
  });
}

const path = (id: string) => ({ params: { path: { webhook_id: id } } });

export const useCreateWebhook = () => useWrite((body: WebhookCreate) => unwrap(api.POST('/api/integrations/webhooks', { body })));

export const useUpdateWebhook = (id: string) =>
  useWrite((body: WebhookPatch) => unwrap(api.PATCH('/api/integrations/webhooks/{webhook_id}', { ...path(id), body })));

export const useDeleteWebhook = () => useWrite((id: string) => unwrap(api.DELETE('/api/integrations/webhooks/{webhook_id}', path(id))));

export const useEnableWebhook = () => useWrite((id: string) => unwrap(api.POST('/api/integrations/webhooks/{webhook_id}/enable', path(id))));

export const useDisableWebhook = () =>
  useWrite(({ id, reason }: { id: string; reason: string }) =>
    unwrap(api.POST('/api/integrations/webhooks/{webhook_id}/disable', { ...path(id), body: { reason } })),
  );

export const useTestWebhook = () => useWrite((id: string) => unwrap(api.POST('/api/integrations/webhooks/{webhook_id}/test', path(id))));

export const useRotateSecret = () => useWrite((id: string) => unwrap(api.POST('/api/integrations/webhooks/{webhook_id}/rotate-secret', path(id))));

export const useRetryDelivery = () =>
  useWrite((id: string) => unwrap(api.POST('/api/integrations/deliveries/{delivery_id}/retry', { params: { path: { delivery_id: id } } })));
